import { ref, onMounted, onUnmounted, watch, type Ref, type ComponentPublicInstance } from 'vue'
import { gsap } from 'gsap'

interface PropsDetailByScreen {
  screen_size?: string | number
  zoom?: number
  src?: string | string[]
}

interface ScreenProps {
  mobile_s?: PropsDetailByScreen
  mobile_m?: PropsDetailByScreen
  mobile_l?: PropsDetailByScreen
  tablet?: PropsDetailByScreen
  laptop?: PropsDetailByScreen
  laptop_l?: PropsDetailByScreen
  desktop?: PropsDetailByScreen
  k_screen?: PropsDetailByScreen
}

export interface BackgroundOptions {
  screen_props?: Partial<ScreenProps>
  style?: Record<string, any>
  interval?: number // en ms pour le slideshow, ex: 3000
  transitionDuration?: number // durée de transition en secondes
  transitionType?: 'fade' | 'crossfade' | 'scale' | 'slideUp' | 'slideDown'
}

const formatWidth = (value: string | number): string =>
  typeof value === 'number' ? `${value}px` : value

const getScreenConfig = (screenProps?: Partial<ScreenProps>): PropsDetailByScreen => {
  const w = window.innerWidth

  const defaultConfig: PropsDetailByScreen = {
    screen_size: '100%',
    zoom: 1,
    src: undefined
  }

  if (w <= 320) return { ...defaultConfig, ...screenProps?.mobile_s }
  if (w <= 375) return { ...defaultConfig, ...screenProps?.mobile_m }
  if (w <= 425) return { ...defaultConfig, ...screenProps?.mobile_l }
  if (w <= 768) return { ...defaultConfig, ...screenProps?.tablet }
  if (w <= 1024) return { ...defaultConfig, ...screenProps?.laptop }
  if (w <= 1440) return { ...defaultConfig, ...screenProps?.laptop_l }
  if (w <= 1920) return { ...defaultConfig, ...screenProps?.desktop }
  if (w <= 2560) return { ...defaultConfig, ...screenProps?.k_screen }

  return defaultConfig
}

// Helper pour extraire l'élément DOM réel d'une ref (support composant Vue ou HTMLElement)
const getElementFromRef = (ref: any): HTMLElement | null => {
  if (!ref) return null
  
  // Si c'est un HTMLElement direct
  if (ref instanceof HTMLElement) {
    return ref
  }
  
  // Si c'est un composant Vue qui expose sectionRef (comme SectionWrapper)
  // SectionWrapper expose { sectionRef: Ref<HTMLElement | null> }
  if (ref && typeof ref === 'object' && 'sectionRef' in ref) {
    const sectionRef = ref.sectionRef
    // sectionRef peut être une Ref (avec .value) ou directement l'élément
    if (sectionRef) {
      if (sectionRef instanceof HTMLElement) {
        return sectionRef
      }
      // Si c'est une Ref Vue, accéder à .value
      if (typeof sectionRef === 'object' && 'value' in sectionRef) {
        return sectionRef.value as HTMLElement | null
      }
    }
  }
  
  // Si c'est un composant Vue avec $el (fallback pour compatibilité)
  if (ref && typeof ref === 'object' && '$el' in ref) {
    const el = ref.$el
    if (el instanceof HTMLElement) {
      return el
    }
  }
  
  return null
}

export const useCenteredBackgroundImage = <T extends HTMLElement | ComponentPublicInstance>(
  elementRef: Ref<T | null>,
  fallbackSrc: string,
  options?: BackgroundOptions
) => {
  const indexRef = ref(0)
  const intervalRef = ref<ReturnType<typeof setInterval> | null>(null)
  const timelineRef = ref<gsap.core.Timeline | null>(null)
  const overlayRef = ref<HTMLDivElement | null>(null)
  
  // Fonction helper pour obtenir l'élément DOM réel
  const getElement = (): HTMLElement | null => {
    return getElementFromRef(elementRef.value)
  }

  const applyBackground = (src: string, targetElement: HTMLElement) => {
    const config = getScreenConfig(options?.screen_props)
    const baseWidth = formatWidth(config.screen_size || '100%')
    const zoom = config.zoom || 1

    let backgroundSize = baseWidth

    if (baseWidth.endsWith('%')) {
      const percent = parseFloat(baseWidth)
      backgroundSize = `${percent * zoom}%`
    } else if (baseWidth.endsWith('px')) {
      const px = parseFloat(baseWidth)
      backgroundSize = `${px * zoom}px`
    }

    Object.assign(targetElement.style, {
      backgroundImage: `url(${src})`,
      backgroundSize,
      backgroundRepeat: 'no-repeat',
      backgroundPosition: 'center center',
      ...options?.style
    })
  }

  const animateTransition = (newSrc: string) => {
    const overlay = overlayRef.value
    const element = getElement()
    if (!overlay || !element) return

    const transitionDuration = options?.transitionDuration || 0.8
    const transitionType = options?.transitionType || 'crossfade'

    // Nettoyer l'ancienne timeline
    if (timelineRef.value) {
      timelineRef.value.kill()
    }

    // Créer une nouvelle timeline
    timelineRef.value = gsap.timeline()

    switch (transitionType) {
      case 'fade':
        // Appliquer la nouvelle image à l'overlay
        applyBackground(newSrc, overlay)
        gsap.set(overlay, { opacity: 0 })

        timelineRef.value
          .to(overlay, {
            opacity: 1,
            duration: transitionDuration,
            ease: 'power2.inOut'
          })
          .call(() => {
            // Transférer l'image vers l'élément principal
            applyBackground(newSrc, element)
            gsap.set(overlay, { opacity: 0 })
          })
        break

      case 'crossfade':
        applyBackground(newSrc, overlay)
        gsap.set(overlay, { opacity: 0 })

        timelineRef.value
          .to(element, {
            opacity: 0.7, // Réduit de 0.3 à 0.7 pour une transition plus subtile
            duration: transitionDuration / 2,
            ease: 'power2.out'
          })
          .to(overlay, {
            opacity: 1,
            duration: transitionDuration / 2,
            ease: 'power2.in'
          }, '-=0.1')
          .call(() => {
            applyBackground(newSrc, element)
            gsap.set([element, overlay], { opacity: 1 })
            gsap.set(overlay, { opacity: 0 })
          })
        break

      case 'scale':
        applyBackground(newSrc, overlay)
        gsap.set(overlay, { opacity: 0, scale: 1.1 })

        timelineRef.value
          .to(element, {
            scale: 0.98, // Réduit de 0.95 à 0.98 pour un effet plus subtil
            opacity: 0.8, // Augmenté de 0.5 à 0.8 pour une transition moins marquée
            duration: transitionDuration / 2,
            ease: 'power2.out'
          })
          .to(overlay, {
            opacity: 1,
            scale: 1,
            duration: transitionDuration / 2,
            ease: 'power2.inOut'
          }, '-=0.1')
          .call(() => {
            applyBackground(newSrc, element)
            gsap.set([element, overlay], { scale: 1, opacity: 1 })
            gsap.set(overlay, { opacity: 0 })
          })
        break

      case 'slideUp':
        applyBackground(newSrc, overlay)
        gsap.set(overlay, { opacity: 1, y: '100%' })

        timelineRef.value
          .to(overlay, {
            y: '0%',
            duration: transitionDuration,
            ease: 'power2.inOut'
          })
          .call(() => {
            applyBackground(newSrc, element)
            gsap.set(overlay, { opacity: 0, y: '0%' })
          })
        break

      case 'slideDown':
        applyBackground(newSrc, overlay)
        gsap.set(overlay, { opacity: 1, y: '-100%' })

        timelineRef.value
          .to(overlay, {
            y: '0%',
            duration: transitionDuration,
            ease: 'power2.inOut'
          })
          .call(() => {
            applyBackground(newSrc, element)
            gsap.set(overlay, { opacity: 0, y: '0%' })
          })
        break

      default:
        applyBackground(newSrc, element)
    }
  }

  const preloadImages = (srcs: string[]) => {
    srcs.forEach(src => {
      const img = new Image()
      img.src = src
    })
  }

  const handleResize = () => {
    const element = getElement()
    if (!element) return

    const config = getScreenConfig(options?.screen_props)
    const srcList = Array.isArray(config.src) ? config.src : [config.src || fallbackSrc]
    const currentIndex = Math.max(0, (indexRef.value - 1) % srcList.length)
    
    applyBackground(srcList[currentIndex], element)

    // Réappliquer les styles à l'overlay
    if (overlayRef.value) {
      const baseWidth = formatWidth(config.screen_size || '100%')
      const zoom = config.zoom || 1

      let backgroundSize = baseWidth
      if (baseWidth.endsWith('%')) {
        const percent = parseFloat(baseWidth)
        backgroundSize = `${percent * zoom}%`
      } else if (baseWidth.endsWith('px')) {
        const px = parseFloat(baseWidth)
        backgroundSize = `${px * zoom}px`
      }

      overlayRef.value.style.backgroundSize = backgroundSize
    }
  }

  const setupBackground = () => {
    const element = getElement()
    if (!element) return

    // Créer un overlay pour les transitions smooth
    if (!overlayRef.value) {
      const overlay = document.createElement('div')
      overlay.style.cssText = `
        position: absolute;
        top: 0;
        left: 0;
        width: 100%;
        height: 100%;
        opacity: 0;
        pointer-events: none;
        z-index: -1;
      `
      element.appendChild(overlay)
      overlayRef.value = overlay
      
      // S'assurer que l'élément parent est positionné relativement
      if (getComputedStyle(element).position === 'static') {
        element.style.position = 'relative'
      }
    }

    const config = getScreenConfig(options?.screen_props)
    const srcList = Array.isArray(config.src) ? config.src : [config.src || fallbackSrc]
    const interval = options?.interval ?? 5000

    if (srcList.length > 1) {
      preloadImages(srcList)
      
      // Appliquer la première image sans animation
      applyBackground(srcList[0], element)
      indexRef.value = 1

      // Nettoyer l'ancien interval
      if (intervalRef.value) {
        clearInterval(intervalRef.value)
      }

      intervalRef.value = setInterval(() => {
        const nextSrc = srcList[indexRef.value % srcList.length]
        animateTransition(nextSrc)
        indexRef.value++
      }, interval)
    } else {
      applyBackground(srcList[0], element)
    }
    }

  const cleanup = () => {
    if (intervalRef.value) {
      clearInterval(intervalRef.value)
      intervalRef.value = null
    }
    if (timelineRef.value) {
      timelineRef.value.kill()
      timelineRef.value = null
    }
      
      // Nettoyer l'overlay
    if (overlayRef.value && overlayRef.value.parentNode) {
      overlayRef.value.parentNode.removeChild(overlayRef.value)
      overlayRef.value = null
      }
  }

  onMounted(() => {
    setupBackground()
    window.addEventListener('resize', handleResize)
  })

  onUnmounted(() => {
    window.removeEventListener('resize', handleResize)
    cleanup()
  })

  // Watch pour les changements d'options
  watch(
    () => [
      elementRef.value,
      getElement(),
      fallbackSrc,
      options?.screen_props,
      options?.interval,
      options?.transitionDuration,
      options?.transitionType
    ],
    () => {
      cleanup()
      setupBackground()
    },
    { deep: true }
  )

  return {
    applyBackground,
    animateTransition,
    cleanup
  }
}

export default useCenteredBackgroundImage
